import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SearchSelect } from '@pattern-library/select';

const propTypes = {
    selectProps: PropTypes.shape({
        defaultOptions: PropTypes.bool,
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        hideLabel: PropTypes.bool,
        errorMessage: PropTypes.string,
        isDisabled: PropTypes.bool,
        isRequired: PropTypes.bool,
        loadingMessage: PropTypes.string,
        noOptionsMessage: PropTypes.string,
        placeholder: PropTypes.string
    }),
    selectedItem: PropTypes.shape({
        description: PropTypes.string,
        value: PropTypes.string,
        type: PropTypes.string
    }),
    getPlansAndCompaniesUrl: PropTypes.string.isRequired,
    addToWorkingSetUrl: PropTypes.string.isRequired
};

const defaultProps = {};

function InternalPlanPicker({
    selectProps: { loadingMessage, noOptionsMessage, ...restSelectProps },
    selectedItem, getPlansAndCompaniesUrl: getPlansAndCompaniesUrlString,
    addToWorkingSetUrl: addToWorkingSetUrlString
}) {
    const [value, setValue] = useState(selectedItem.description && selectedItem.value ? selectedItem : { description: '', value: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const showLessThanThreeCharsMessage = useCallback((queryString, message) => {
        if (queryString.inputValue.length < 3) {
            return "Please type 3 or more characters";
        } else {
            return message;
        }
    }, [loadingMessage, noOptionsMessage]);

    const loadOptions = useCallback((queryString) => {
        if (queryString.length < 3) {
            setHasError(false);
            return;
        } else {
            return new Promise(async (resolve, reject) => {
                const urlSearchParams = new URLSearchParams({ searchValue: queryString });
                const getPlansUrl = `${getPlansAndCompaniesUrlString}?${urlSearchParams.toString()}`;

                setIsLoading(true);

                try {
                    const res = await fetch(getPlansUrl, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });

                    if (!res.ok) {
                        setHasError(true);
                        reject();
                    }

                    const data = await res.json();

                    resolve(data);
                }
                catch (e) {
                    setHasError(true);
                    reject();
                }

                setIsLoading(false);
            });
        }
    }, [setHasError, setIsLoading, getPlansAndCompaniesUrlString]);

    const onChange = useCallback((option) => {
        if (!option) {
            setValue({ description: '', value: '', type: '' });
            return;
        }

        npg.showBusyIndicator();

        setValue(option);

        const { value, type } = option;
        const urlSearchParams = new URLSearchParams({ value, type });

        window.location.href = `${addToWorkingSetUrlString}?${urlSearchParams.toString()}`;
    }, [addToWorkingSetUrlString]);

    return (
        <div className="dca-plan-picker">
            <SearchSelect
                id="dca-internal-plan-picker"
                label="Pick Plan"
                isLoading={isLoading}
                hasError={hasError}
                loadOptions={loadOptions}
                loadingMessage={(queryString) => showLessThanThreeCharsMessage(queryString, loadingMessage)}
                noOptionsMessage={(queryString) => showLessThanThreeCharsMessage(queryString, noOptionsMessage)}
                onChange={onChange}
                reactSelectProps={{
                    getOptionLabel: (option) => (option.description)
                }}
                value={value}
                {...restSelectProps}
            />
        </div>
    );
}

InternalPlanPicker.propTypes = propTypes;
InternalPlanPicker.defaultProps = defaultProps;

export default InternalPlanPicker;